import React, { useState } from 'react';
import { simboloMoneda } from '../services/negocioActual';
import { actualizarCuenta, ajustarSaldo, desactivarCuenta, TIPOS_CUENTA } from '../services/cuentasService';
import { mensajeAmigable } from '../utils/errores';
import { aNumero } from '../utils/validacion';
import CampoNumero from './CampoNumero';
import { useToast } from '../context/ToastContext';

const fmt = (n) => {
  const v = Number(n || 0);
  return `${v < 0 ? '-' : ''}${simboloMoneda()} ${Math.abs(v).toLocaleString('es-HN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Editar una cuenta desde la vista de Cuentas: nombre, tipo, corregir el
// saldo real (deja registro de ajuste) y desactivar (ocultar sin borrar).
export default function EditarCuentaModal({ cuenta, onCerrar, onGuardado }) {
  const { mostrarToast } = useToast();
  const [nombre, setNombre] = useState(cuenta.nombre || '');
  const [tipo, setTipo] = useState(cuenta.tipo || 'banco');
  const [saldo, setSaldo] = useState(String(cuenta.saldo ?? ''));
  const [guardando, setGuardando] = useState(false);
  const [confirmarBaja, setConfirmarBaja] = useState(false);

  const guardar = async () => {
    if (!nombre.trim()) { mostrarToast('Escribe el nombre de la cuenta', 'error'); return; }
    setGuardando(true);
    try {
      // 1) Nombre y tipo (si cambiaron)
      if (nombre.trim() !== cuenta.nombre || tipo !== cuenta.tipo) {
        await actualizarCuenta(cuenta.id, { nombre, tipo });
      }
      // 2) Saldo (si cambió → ajuste con registro)
      const saldoNuevo = aNumero(saldo);
      if (saldoNuevo !== null && saldoNuevo !== Number(cuenta.saldo)) {
        await ajustarSaldo(cuenta.id, saldoNuevo, 'Corrección de saldo (edición)');
      }
      mostrarToast('Cuenta actualizada ✅', 'exito');
      onGuardado?.();
      onCerrar();
    } catch (e) {
      mostrarToast(mensajeAmigable(e), 'error');
    } finally {
      setGuardando(false);
    }
  };

  const desactivar = async () => {
    setGuardando(true);
    try {
      await desactivarCuenta(cuenta.id);
      mostrarToast('Cuenta ocultada. Su historial se conserva.', 'exito');
      onGuardado?.();
      onCerrar();
    } catch (e) {
      mostrarToast(mensajeAmigable(e), 'error');
    } finally {
      setGuardando(false);
    }
  };

  const input = { width: '100%', padding: '10px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' };
  const label = { fontSize: '12px', color: '#6B7280', fontWeight: 600, display: 'block', marginBottom: '4px' };

  return (
    <div onClick={onCerrar} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', zIndex: 1200 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: 'white', borderRadius: '16px', width: '100%', maxWidth: '420px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #F3F4F6', backgroundColor: '#FFF5F7', borderRadius: '16px 16px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, color: 'var(--color-primario,#DB2777)', fontSize: '18px' }}>✏️ Editar cuenta</h3>
          <button onClick={onCerrar} disabled={guardando} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer' }}>✕</button>
        </div>

        <div style={{ padding: '20px' }}>
          <div style={{ marginBottom: '12px' }}>
            <label style={label}>Nombre de la cuenta</label>
            <input value={nombre} disabled={guardando} onChange={(e) => setNombre(e.target.value)} style={input} placeholder="Ej. BAC ahorro, POS Visa" />
          </div>

          <div style={{ marginBottom: '12px' }}>
            <label style={label}>Tipo de cuenta</label>
            <select value={tipo} disabled={guardando} onChange={(e) => setTipo(e.target.value)} style={input}>
              {TIPOS_CUENTA.map((t) => <option key={t.valor} value={t.valor}>{t.icono} {t.etiqueta}</option>)}
            </select>
          </div>

          <div style={{ marginBottom: '4px' }}>
            <label style={label}>Saldo real (L)</label>
            <CampoNumero valor={saldo} onChange={setSaldo} deshabilitado={guardando} negativos prefijo="L" estilo={input} />
            <p style={{ margin: '6px 0 0', fontSize: '11px', color: '#9CA3AF' }}>
              Saldo actual: <strong>{fmt(cuenta.saldo)}</strong>. Si lo cambiás, se registra un ajuste. Para tarjeta de crédito, la deuda va en negativo (ej. -5000).
            </p>
          </div>

          <div style={{ display: 'flex', gap: '10px', marginTop: '18px' }}>
            <button onClick={onCerrar} disabled={guardando}
              style={{ flex: '0 0 auto', padding: '12px 18px', background: 'white', border: '1px solid #D1D5DB', borderRadius: '10px', cursor: 'pointer', color: '#6B7280', fontWeight: 600 }}>
              Cancelar
            </button>
            <button onClick={guardar} disabled={guardando}
              style={{ flex: 1, padding: '12px', backgroundColor: guardando ? '#9CA3AF' : 'var(--color-primario,#DB2777)', color: 'white', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: 'bold', cursor: guardando ? 'wait' : 'pointer' }}>
              {guardando ? '⏳ Guardando...' : '💾 Guardar cambios'}
            </button>
          </div>

          {/* Desactivar cuenta */}
          <div style={{ marginTop: '18px', paddingTop: '16px', borderTop: '1px solid #F3F4F6' }}>
            {!confirmarBaja ? (
              <button onClick={() => setConfirmarBaja(true)} disabled={guardando}
                style={{ width: '100%', padding: '10px', background: 'white', color: '#DC2626', border: '1px solid #FCA5A5', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer' }}>
                🗑️ Desactivar esta cuenta
              </button>
            ) : (
              <div style={{ padding: '12px', backgroundColor: '#FEF2F2', borderRadius: '8px', border: '1px solid #FCA5A5' }}>
                <p style={{ margin: '0 0 10px', fontSize: '13px', color: '#991B1B', fontWeight: 600, textAlign: 'center' }}>
                  ¿Ocultar "{cuenta.nombre}"? No se borra: deja de aparecer, pero su historial y movimientos se conservan.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <button onClick={() => setConfirmarBaja(false)} disabled={guardando}
                    style={{ padding: '10px', backgroundColor: 'white', color: '#374151', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer' }}>
                    No, cancelar
                  </button>
                  <button onClick={desactivar} disabled={guardando}
                    style={{ padding: '10px', backgroundColor: guardando ? '#9CA3AF' : '#DC2626', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer' }}>
                    {guardando ? '⏳...' : 'Sí, ocultar'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
